import Highcharts from "highcharts";
import { renderToString } from "react-dom/server";
import { Theme } from "@mui/material";
import { toAmericanShort } from "@helpers";
import { GoalStatus } from "../../utils/goalStatus";

export const GOAL_CHART_HEIGHT = { desktop: 330, mobile: 240 };

export const axisLabelStyle = (theme: Theme): Highcharts.CSSObject => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.caption.fontSize as string,
  color: theme.palette.text.secondary,
  fontWeight: "400",
});

export const goalYAxis = (theme: Theme): Highcharts.YAxisOptions => ({
  title: { text: "" },
  min: 0,
  tickAmount: 5,
  gridLineColor: theme.palette.border.secondary,
  labels: {
    style: axisLabelStyle(theme),
    formatter: function () {
      return toAmericanShort(Number(this.value), 0, 2);
    },
  },
});

export const seriesColors = (theme: Theme) => ({
  wealth: theme.palette.graphVisual["02"].mid,
  plan: theme.palette.graphVisual["01"].mid,
});

const statusColors = (theme: Theme, status: GoalStatus) => {
  switch (status) {
    case "behindPlan":
      return {
        line: theme.palette.icon.warning,
        background: theme.palette.background.warning,
        text: theme.palette.text.warning,
      };
    case "atRisk":
      return {
        line: theme.palette.icon.error,
        background: theme.palette.background.error,
        text: theme.palette.text.negative,
      };
    default:
      return {
        line: theme.palette.icon.success,
        background: theme.palette.background.success,
        text: theme.palette.text.positiveOnBadge,
      };
  }
};

export const gapLineColor = (theme: Theme, status: GoalStatus) =>
  statusColors(theme, status).line;

/**
 * The plan's target and its label, e.g. "Ziel € 600.000". The plan's path
 * misses it by a little: the required yield is an internal rate of return,
 * the projection the backend's simple interest per year.
 */
export interface GoalTarget {
  value: number;
  label: string;
}

/** The target's label at the end of a plan */
export const planEndLabel = (
  theme: Theme,
  text: string
): Highcharts.DataLabelsOptions => ({
  enabled: true,
  crop: false,
  formatter: () => text,
  backgroundColor: theme.palette.background.paper,
  padding: 2,
  style: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize as string,
    fontWeight: "600",
    color: theme.palette.text.primary,
    textOutline: "none",
  },
});

/** The "+ € 5.472 über Plan" chip at today's gap, in the status colours */
export const gapChipLabel = (
  theme: Theme,
  status: GoalStatus,
  text: string
): Highcharts.DataLabelsOptions => {
  const colors = statusColors(theme, status);
  return {
    enabled: true,
    useHTML: true,
    crop: false,
    overflow: "allow",
    formatter: () =>
      renderToString(
        <span
          style={{
            display: "inline-block",
            padding: theme.spacing(0.75, 2),
            borderRadius: theme.shape.borderRadius,
            background: colors.background,
            color: colors.text,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.caption.fontSize,
            lineHeight: theme.typography.caption.lineHeight,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </span>
      ),
  };
};
